// When a message counts as READ, and which seq to report.
//
// Pure on purpose: these are the rules that decide whether a blue tick is
// truthful, and they should be testable without a DOM or a network.

/** The optimistic seq a just-sent message carries until the server's lands. */
export const OPTIMISTIC_SEQ = Number.MAX_SAFE_INTEGER;

export interface SeqBearing {
  seq: number;
}

/**
 * The highest seq that actually exists on the server.
 *
 * A just-sent message sits in the list with seq = MAX_SAFE_INTEGER so it sorts
 * to the bottom until its real seq arrives. Taking the last element's seq
 * therefore reported 9007199254740991 every time you sent a message, and since
 * the server advances with GREATEST, the cursor pinned at the maximum for ever:
 * unread died for that conversation and every later message arrived
 * pre-marked as read. Optimistic entries are excluded, and the list is NOT
 * assumed sorted.
 */
export function readHighWaterMark(messages: readonly SeqBearing[]): number {
  let max = 0;
  for (const m of messages) {
    const s = m?.seq;
    if (typeof s !== 'number' || !Number.isFinite(s)) continue;
    if (s === OPTIMISTIC_SEQ) continue;
    if (s > max) max = s;
  }
  return max;
}

export interface ReadGate {
  /** The tab is foregrounded (document.visibilityState === 'visible'). */
  visible: boolean;
  /** The conversation is actually on screen, not merely loaded in state. */
  conversationOpen: boolean;
  /** Highest real seq currently rendered. */
  seq: number;
  /** Highest seq already reported, so a repeat is not re-sent. */
  lastSent: number;
}

/**
 * Whether to report a read high-water mark now.
 *
 * Loading a conversation is not reading it. A push arriving is not reading it.
 * A payload landing in state while the tab sits in the background is not
 * reading it. All three previously marked messages read, which is what made the
 * blue tick meaningless.
 *
 * Only moves forward: the server clamps with GREATEST anyway, but not sending a
 * backwards or repeated value keeps a 12s reconcile from writing a mark and
 * emitting an event on every tick.
 */
export function shouldAdvanceRead(g: ReadGate): boolean {
  if (!g.visible || !g.conversationOpen) return false;
  if (!Number.isFinite(g.seq) || g.seq <= 0) return false;
  return g.seq > g.lastSent;
}

/**
 * Whether to acknowledge delivery now.
 *
 * Deliberately NOT gated on visibility. Delivered means the client received the
 * message — true whether or not anyone is looking at the tab, and it is what
 * separates "it reached them" from "they read it". Gating it on focus would
 * collapse the two states into one.
 */
export function shouldAcknowledgeDelivery(seq: number, lastSent: number): boolean {
  if (!Number.isFinite(seq) || seq <= 0) return false;
  return seq > lastSent;
}

/** Aggregate receipt cursors for one conversation, as the server reports them. */
export interface ConversationReceipts {
  min_delivered_seq: number;
  min_read_seq: number;
  recipients: number;
}

export type Tick = 'sent' | 'delivered' | 'read';

/**
 * The tick for one of MY messages. Mirrors the server's TickFor exactly — the
 * server stays authoritative and this only renders what it reports, so the two
 * cannot drift into showing different things.
 *
 * Returns 'sent' for an optimistic message too: it has no server seq yet, so
 * nothing can have been delivered to anyone.
 */
export function tickFor(seq: number, r: ConversationReceipts | undefined): Tick {
  if (!r || r.recipients <= 0) return 'sent';
  if (seq === OPTIMISTIC_SEQ || !Number.isFinite(seq) || seq <= 0) return 'sent';
  if (r.min_read_seq >= seq) return 'read';
  if (r.min_delivered_seq >= seq) return 'delivered';
  return 'sent';
}

/** One recipient's position on a specific message (Message Info). */
export interface ParticipantReceipt {
  user_id: string;
  delivered_at?: string;
  read_at?: string;
}

/** Message Info for one message, as the server reports it. */
export interface MessageReceipts {
  message_id: string;
  seq: number;
  /** Excludes the sender — they did not receive their own message. */
  recipients: number;
  delivered_count: number;
  read_count: number;
  /** Absent above the server's participant cap. */
  detail?: ParticipantReceipt[];
  /** The breakdown was withheld for size, not because nobody has seen it. */
  detail_suppressed?: boolean;
}

/**
 * Split a Message Info breakdown into the two groups the panel shows, each
 * ordered by when it happened.
 *
 * Readers first and oldest-first within each group, so the list reads as the
 * order people actually got to it rather than whatever order the database
 * returned rows in. Someone who has only received it sits under "Delivered to";
 * someone with neither has genuinely not got it yet and appears in neither,
 * which is why the counts beside the lists are the authoritative numbers.
 */
export function splitReceipts(detail: readonly ParticipantReceipt[] | undefined): {
  read: ParticipantReceipt[];
  deliveredOnly: ParticipantReceipt[];
} {
  const read: ParticipantReceipt[] = [];
  const deliveredOnly: ParticipantReceipt[] = [];
  for (const p of detail ?? []) {
    if (p.read_at) read.push(p);
    // read implies delivered, so a reader is never also listed here.
    else if (p.delivered_at) deliveredOnly.push(p);
  }
  const byTime = (k: 'read_at' | 'delivered_at') => (a: ParticipantReceipt, b: ParticipantReceipt) =>
    String(a[k] ?? '').localeCompare(String(b[k] ?? ''));
  read.sort(byTime('read_at'));
  deliveredOnly.sort(byTime('delivered_at'));
  return { read, deliveredOnly };
}
